/**
 * Wattec Custom PC Builder — Easify adapter
 *
 * Easify Product Options stays in charge of options, prices, rules and the
 * cart. This file only reads what Easify already knows and drives the
 * controls Easify already renders. Nothing here calculates a price of its
 * own, and nothing here builds a cart.
 *
 * This adapter previously targeted Aris Product Options. The store has since
 * switched to Easify, so every method below reads Easify's own data and
 * markup instead — but the public shape (method names, what each returns) is
 * kept identical on purpose, because wattec-pc-builder-ui.js,
 * wattec-pc-power.js and wattec-pc-builder-save.js were all deliberately
 * written against this adapter's interface rather than against either
 * options app directly. None of those three files needed to change for this
 * migration; only what happens inside this file did.
 *
 * Everything below was derived from the live Easify output on this store,
 * and from Easify's own public rendering bundle (loaded from
 * cdn.shopify.com/extensions/.../product-options-*.js), since Easify renders
 * its controls with client-side JavaScript rather than in the page's initial
 * HTML:
 *
 *   window.TPOConfigs.product_options_easify     full option data, inlined per product
 *   .tpo_option-set-container                    root Easify renders all of its controls into
 *   #<option.id>,<option.type>                   one option group's own container element
 *   .tpo_hidden                                  the class Easify toggles to hide a group
 *   input[name="<option.id>,<option.type>"]      a real radio/checkbox in a "buttons" group;
 *                                                 its own value is the choice's label text
 *   input.tpo_option-dropdown                    the visible field for a "dropdown" group,
 *                                                 holding "<label> [ +£X.XX ]" once chosen
 *   .tpo_additional-price                        the price shown next to a buttons/swatch value
 *   .tpo_option-required-indicator                the "*" shown on a required group
 */
(function () {
  'use strict';

  var VERSION = '1.0.0';

  /* ------------------------------------------------------------------
   * Mapping layer
   *
   * Easify option titles differ between the three custom PC products
   * ("AM4 CPUs" vs "14th Gen CPUs" vs "Select Your CPU"), so sections are
   * matched on patterns rather than exact names. A new product, or a
   * renamed option, is picked up without touching anything else. Order
   * matters: the first pattern that matches wins, so "CPU Cooler" must be
   * tested before "CPU".
   * ------------------------------------------------------------------ */
  var SECTIONS = [
    { key: 'case_size',   title: 'Case Size',        pattern: /^\s*case\s*$|choose your case/i },
    { key: 'cooling',     title: 'CPU Cooling',      pattern: /cooler|cooling/i },
    { key: 'platform',    title: 'Platform',         pattern: /platform|intel gen|generation/i },
    // "cpus?" not "\bcpu\b" — several products label this option "AM4 CPUs" /
    // "14th Gen CPUs" (plural), which \bcpu\b never matched because the word
    // boundary check fails partway through "cpus". Those fell through to
    // "Extras" instead of "Processor" until this was corrected.
    { key: 'cpu',         title: 'Processor',        pattern: /\bcpus?\b|processor/i },
    { key: 'motherboard', title: 'Motherboard',      pattern: /motherboard/i },
    { key: 'memory',      title: 'Memory',           pattern: /\bram\b|memory/i },
    { key: 'storage',     title: 'Storage',          pattern: /storage|ssd|nvme|hard drive/i },
    { key: 'gpu',         title: 'Graphics Card',    pattern: /graphic|\bgpu\b/i },
    { key: 'psu',         title: 'Power Supply',     pattern: /power supply|\bpsu\b/i },
    // Fans before case: the "Case Fans" chooser (regular or custom fans)
    // names the case too, and was being filed under the case step.
    { key: 'fans',        title: 'Case Fans',        pattern: /\bfans?\b/i },
    { key: 'case',        title: 'Case',             pattern: /case/i },
    { key: 'os',          title: 'Operating System', pattern: /windows|operating system/i },
    { key: 'monitor',     title: 'Monitor',          pattern: /\bmonitor\b/i },
    { key: 'keyboard',    title: 'Keyboard',         pattern: /\bkeyboard\b/i },
    { key: 'mouse',       title: 'Mouse',            pattern: /\bmouse\b/i },
    { key: 'headphones',  title: 'Headphones',       pattern: /\bheadphones?\b/i },
    { key: 'extras',      title: 'Extras',           pattern: /.*/ }
  ];

  function sectionFor(label) {
    var name = String(label || '');
    for (var i = 0; i < SECTIONS.length; i++) {
      if (SECTIONS[i].pattern.test(name)) return SECTIONS[i];
    }
    return SECTIONS[SECTIONS.length - 1];
  }

  /* ------------------------------------------------------------------
   * Small helpers
   * ------------------------------------------------------------------ */

  function settings() {
    return window.TPOConfigs || null;
  }

  function container() {
    return document.querySelector('.tpo_option-set-container');
  }

  function toNumber(value) {
    var n = parseFloat(value);
    return isNaN(n) ? 0 : n;
  }

  function config() {
    var root = document.getElementById('wattec-pc-builder-config');
    if (!root) return {};
    try {
      return JSON.parse(root.textContent) || {};
    } catch (error) {
      return {};
    }
  }

  /** A value's own metadata, parsed once. Holds the description/tooltip text
   *  every compatibility check and the power estimator read as "spec". */
  function metadataOf(valueType) {
    if (!valueType || !valueType.metadata) return {};
    try {
      return JSON.parse(valueType.metadata) || {};
    } catch (error) {
      return {};
    }
  }

  /**
   * Strips a dropdown's own "<label> [ +£X.XX ]" (or "( +£X.XX )", or a
   * trailing " | <qty>" for a quantity-enabled option) display text down to
   * the plain label it started as — the same shape the floating Add to Cart
   * button's own script already parses reliably in production, reused here
   * rather than re-invented.
   */
  function parsePickedLabel(text) {
    var raw = String(text || '').trim();
    if (!raw) return '';
    raw = raw.replace(/\s*\|\s*\d+\s*$/, '');
    raw = raw.replace(/\s*[\[(]\s*\+?\s*£?\s*[\d,]+(?:\.\d+)?\s*[\])]\s*$/, '');
    return raw.trim();
  }

  /** The component a radio/checkbox stands for. A "buttons" group's input
   *  holds the plain label; a "Checkbox" option's holds the label with its
   *  price appended ("<label> [ +£32.99 ]"), which is stripped the same way
   *  a dropdown's field is. */
  function inputLabel(input) {
    return parsePickedLabel(input && input.value);
  }

  /* ------------------------------------------------------------------
   * Easify option-set discovery
   * ------------------------------------------------------------------ */

  function optionSets() {
    var s = settings();
    return (s && s.product_options_easify) || [];
  }

  function currentProductGid() {
    var id = config().productId;
    return id ? ('gid://shopify/Product/' + id) : null;
  }

  /**
   * Easify inlines every option set the shop has, not only the ones that
   * apply to the product on this page (a "Signature Series" set shows up
   * on a "Custom PC" page's own data too). Each set's own ai_manifest
   * carries which products it is actually assigned to, so that is checked
   * before using anything from it — the same principle the old adapter
   * used for Aris's per-set "products" list, kept permissive the same way
   * when a set does not specify: it is included rather than assumed
   * irrelevant.
   */
  function relevantEntries() {
    var gid = currentProductGid();
    return optionSets().filter(function (entry) {
      var ids = entry.ai_manifest && entry.ai_manifest.product_ids;
      if (!ids || !ids.length) return true;
      return !gid || ids.indexOf(gid) !== -1;
    });
  }

  /**
   * The internal key used for a group everywhere in this file (saved-build
   * codes, the outstanding/progress lookups). Kept independent from the
   * group's own rendered DOM id below, which turned out to carry an extra
   * per-render id Easify prepends of its own.
   */
  function groupId(option) {
    return option.id + ',' + option.type;
  }

  /**
   * Easify's real rendered id for a group is
   * "<per-render id>,<option.id>,<option's own raw Easify type>" —
   * confirmed against the live page, not just the option data. The
   * per-render id isn't published anywhere in window.TPOConfigs, so rather
   * than guess it, every group is found by matching the id's own ending
   * instead, which is exactly as reliable and needs nothing extra known
   * about it.
   *
   * Takes the raw Easify type ("buttons"/"dropdown") explicitly, on
   * purpose, rather than reading it off an option object — the option
   * objects this adapter hands to the rest of the builder carry the
   * *translated* type ("button_single") for the UI's own use, and the two
   * must never be mixed up here: this id only ever exists in Easify's own
   * vocabulary.
   */
  function findGroupElement(id, rawType) {
    var suffix = ',' + id + ',' + rawType;
    return document.querySelector('[id$="' + suffix + '"]');
  }

  /** Easify's own vocabulary ("buttons" / "dropdown") translated to the one
   *  this builder's UI already expects — the only place that reads an
   *  option's type is the chooser/list split in wattec-pc-builder-ui.js,
   *  which looks for "button_single". Anything else passes through
   *  unchanged, which is harmless since nothing else keys off a type. */
  function translateType(option) {
    if (option.type === 'buttons') return option.multi_select ? 'button_multi' : 'button_single';
    return option.type;
  }

  /**
   * Presses a control the way a real finger or mouse does.
   *
   * This matters, and it was got wrong before. Easify does not listen for
   * a plain "click" on its option controls — it listens on the pointer and
   * mouse events that lead up to one (pointerdown/mousedown, pointerup/
   * mouseup) as well. Calling element.click() fires only the final click
   * event, so Easify never sees the press at all: the radio stays
   * unchecked, no dependent group opens up and no price changes. That was
   * originally read as proof that Easify ignores anything a script does and
   * only reacts to the customer's own hand, which is not what is happening
   * — replaying the whole sequence below drives Easify exactly as a real
   * press does, conditional reveals and pricing included (verified on this
   * page against the live draft, and again through the cart).
   *
   * Adding a build to the cart is the one thing this does NOT cover: the
   * theme's real Add to Cart button still has to be pressed by the
   * customer themselves, so nothing here ever tries to submit the form.
   */
  function press(node) {
    if (!node) return false;
    var options = {
      bubbles: true, cancelable: true, composed: true, view: window,
      button: 0, buttons: 1, detail: 1,
      pointerId: 1, pointerType: 'mouse', isPrimary: true
    };
    ['pointerover', 'pointerenter', 'pointerdown', 'mousedown', 'pointerup', 'mouseup', 'click']
      .forEach(function (type) {
        var Constructor = type.indexOf('pointer') === 0 ? window.PointerEvent : window.MouseEvent;
        try {
          node.dispatchEvent(new Constructor(type, options));
        } catch (error) {
          // Older browsers without PointerEvent still get the mouse half.
          if (type.indexOf('pointer') !== 0) node.dispatchEvent(new MouseEvent(type, options));
        }
      });
    return true;
  }

  /** Text as it reads to a customer: one line, no double spaces. */
  function flatten(text) {
    return String(text == null ? '' : text).replace(/\s+/g, ' ').trim();
  }

  /* ==================================================================
   * Easify adapter
   * ================================================================== */
  var Easify = {

    version: VERSION,

    /* ---- availability ------------------------------------------- */

    /** True once Easify has published its data and rendered its controls. */
    isReady: function () {
      var entries = relevantEntries();
      if (!entries.length) return false;
      for (var i = 0; i < entries.length; i++) {
        var opts = (entries[i].option_set && entries[i].option_set.options) || [];
        for (var j = 0; j < opts.length; j++) {
          if (findGroupElement(opts[j].id, opts[j].type)) return true;
        }
      }
      return false;
    },

    /**
     * Resolves once Easify is ready. Easify's own render happens async, on
     * its own schedule, so this polls rather than waiting on any single
     * event.
     */
    ready: function (timeoutMs) {
      var limit = timeoutMs || 20000;
      var self = this;
      return new Promise(function (resolve, reject) {
        if (self.isReady()) return resolve(self);
        var started = Date.now();
        var timer = setInterval(function () {
          if (self.isReady()) {
            clearInterval(timer);
            resolve(self);
          } else if (Date.now() - started > limit) {
            clearInterval(timer);
            reject(new Error('Easify options did not load within ' + limit + 'ms'));
          }
        }, 150);
      });
    },

    /* ---- reading the configuration ------------------------------- */

    /**
     * Easify's options, normalised into the shape the builder UI wants.
     * This is a read of Easify's data — it is never stored as a second copy
     * of the truth, so call it again whenever you need it.
     */
    discoverOptions: function () {
      var options = [];
      // Easify inlines more than one option set per page, and the same
      // option can appear in several of them — Wattec's own "Additional
      // Storage 1" is in the custom PC set and in two Signature Series
      // sets, and the peripherals sets come through twice. They are the
      // same option, with the same id and the same single rendered
      // control, so listing each one once is what keeps the build summary
      // (and step progress) from counting one choice repeatedly.
      var seen = {};
      relevantEntries().forEach(function (entry) {
        var set = entry.option_set || {};
        (set.options || []).forEach(function (option, index) {
          if (seen[option.id]) return;
          seen[option.id] = true;
          var section = sectionFor(option.title);
          options.push({
            key: groupId(option),
            id: option.id,
            index: index,
            label: option.title,
            cartLabel: option.title,
            type: translateType(option),
            rawType: option.type,
            required: option.is_required === 1,
            section: section.key,
            sectionTitle: section.title,
            conditions: option.conditions_serialized || null,
            values: (option.option_types || []).map(function (value) {
              var meta = metadataOf(value);
              return {
                id: value.id,
                label: String(value.label || '').trim(),
                price: toNumber(value.additional_price),
                spec: String(meta.description || meta.tooltip || '').trim(),
                image: value.image_url || ''
              };
            })
          });
        });
      });
      return options;
    },

    /** Options grouped into builder sections, in the order the UI shows them. */
    discoverSections: function () {
      var options = this.discoverOptions();
      var buckets = {};
      options.forEach(function (option) {
        if (!buckets[option.section]) buckets[option.section] = [];
        buckets[option.section].push(option);
      });
      return SECTIONS.filter(function (section) {
        return buckets[section.key] && buckets[section.key].length;
      }).map(function (section) {
        return { key: section.key, title: section.title, options: buckets[section.key] };
      });
    },

    /* ---- the rendered controls ----------------------------------- */

    /** The root element Easify renders all of its controls into. */
    getContainer: container,

    /** The element Easify rendered for one option group. */
    elementFor: function (option) {
      return findGroupElement(option.id, option.rawType);
    },

    /** The checked radio/checkbox in a "buttons" group, if any. */
    checkedInputFor: function (option) {
      var el = this.elementFor(option);
      if (!el) return null;
      return el.querySelector('input[type="radio"]:checked, input[type="checkbox"]:checked');
    },

    /** Every checked radio/checkbox in a group. More than one only in an
     *  option that allows several picks (Custom Fans & RGB Cables). */
    checkedInputsFor: function (option) {
      var el = this.elementFor(option);
      if (!el) return [];
      return [].slice.call(el.querySelectorAll('input[type="radio"]:checked, input[type="checkbox"]:checked'));
    },

    /**
     * The quantity box Easify draws beside a value when its option has the
     * quantity selector turned on. Found with the same climb as the price
     * (see priceElementNear), so it is always this value's own box.
     */
    quantityBoxNear: function (input) {
      var node = input.parentElement;
      var depth = 0;
      while (node && depth < 10) {
        if (node.querySelectorAll('input[type="radio"], input[type="checkbox"]').length > 1) break;
        var box = node.querySelector('.tpo_quantity-selector_input');
        if (box) return box;
        node = node.parentElement;
        depth++;
      }
      return null;
    },

    /** How many of a chosen value the customer wants; 1 without a box. */
    quantityNear: function (input) {
      var box = this.quantityBoxNear(input);
      var qty = box ? parseInt(box.value, 10) : 1;
      return isNaN(qty) || qty < 1 ? 1 : qty;
    },

    /** The visible text field a "dropdown" group shows its current pick in. */
    dropdownInputFor: function (option) {
      var el = this.elementFor(option);
      if (!el) return null;
      return el.querySelector('input.tpo_option-dropdown, input[type="text"][name^="properties["]');
    },

    /**
     * Whether Easify is currently showing this option. Easify's conditional
     * logic hides a group by adding the "tpo_hidden" class to its container
     * (the same class this floating Add to Cart button's own script already
     * checks), so this matches Easify the same way rather than guessing.
     */
    isVisible: function (option) {
      var element = this.elementFor(option);
      if (!element) return false;
      if (element.classList && element.classList.contains('tpo_hidden')) return false;
      var style = window.getComputedStyle ? window.getComputedStyle(element) : null;
      if (style && style.display === 'none') return false;
      return true;
    },

    /** Whether Easify has marked a value as unavailable. */
    isDisabled: function (valueId) {
      var found = this.controlFor(valueId);
      return !!(found && (found.disabled || found.hasAttribute('disabled')));
    },

    /**
     * Finds the rendered control for one value, whichever kind of group it
     * belongs to. Returns the <input> for a buttons group, or the visible
     * text field for a dropdown group — callers only ever read/compare
     * against it, so the difference does not need to leak further.
     */
    /** The option/value pair a value id belongs to, wherever it is. */
    findValue: function (valueId) {
      var options = this.discoverOptions();
      for (var i = 0; i < options.length; i++) {
        for (var j = 0; j < options[i].values.length; j++) {
          if (options[i].values[j].id === valueId) return { option: options[i], value: options[i].values[j] };
        }
      }
      return null;
    },

    controlFor: function (valueId) {
      var found = this.findValue(valueId);
      if (!found) return null;
      var option = found.option, label = found.value.label;

      if (translateType(option) === 'dropdown') {
        var field = this.dropdownInputFor(option);
        return (field && parsePickedLabel(field.value) === label) ? field : (field || null);
      }
      var el = this.elementFor(option);
      if (!el) return null;
      var inputs = el.querySelectorAll('input[type="radio"], input[type="checkbox"]');
      for (var k = 0; k < inputs.length; k++) {
        if (inputLabel(inputs[k]) === label) return inputs[k];
      }
      return null;
    },

    /** Has the customer chosen this value? */
    isChosen: function (value, option) {
      if (translateType(option) === 'dropdown') {
        var field = this.dropdownInputFor(option);
        return !!(field && parsePickedLabel(field.value) === value.label);
      }
      return this.checkedInputsFor(option).some(function (checked) { return inputLabel(checked) === value.label; });
    },

    /* ---- reading the current build ------------------------------- */

    /** Everything the customer has chosen so far, in section order. */
    getSelection: function () {
      var self = this;
      var chosen = [];
      this.discoverOptions().forEach(function (option) {
        var visible = self.isVisible(option);
        var picks = [];

        if (translateType(option) === 'dropdown') {
          var field = self.dropdownInputFor(option);
          var label = field ? parsePickedLabel(field.value) : '';
          var qtyMatch = field ? String(field.value || '').match(/\|\s*(\d+)\s*$/) : null;
          if (label) {
            var value = option.values.filter(function (v) { return v.label === label; })[0];
            if (value) picks.push({ value: value, quantity: qtyMatch ? parseInt(qtyMatch[1], 10) : 1 });
          }
        } else {
          // An option that allows several picks has more than one ticked,
          // each listed as its own line with its own quantity.
          self.checkedInputsFor(option).forEach(function (checked) {
            var value = option.values.filter(function (v) { return v.label === inputLabel(checked); })[0];
            if (value) picks.push({ value: value, quantity: self.quantityNear(checked) });
          });
        }

        picks.forEach(function (pick) {
          var picked = pick.value;
          var live = self.getLinePrice(picked.id);
          chosen.push({
            section: option.section,
            sectionTitle: option.sectionTitle,
            optionLabel: option.label,
            cartLabel: option.cartLabel,
            optionKey: option.key,
            required: option.required,
            visible: visible,
            valueId: picked.id,
            label: picked.label,
            quantity: pick.quantity,
            spec: picked.spec,
            // What this line adds to the price: a dropdown's own figure is
            // already the total for its quantity, and a ticked value's is
            // multiplied by its quantity in getLinePrice.
            price: live === null ? picked.price * pick.quantity : live,
            image: picked.image
          });
        });
      });
      return chosen;
    },

    /**
     * What a chosen value actually adds to the price, in pounds. Read from
     * the control Easify rendered, not from the option data, so this can
     * never disagree with what the floating bar is already adding up.
     * Returns null when nothing could be read from the page — callers fall
     * back to the value's own stored price in that case, the same contract
     * this adapter has always had.
     */
    getLinePrice: function (valueId) {
      var found = this.findValue(valueId);
      if (!found) return null;
      var option = found.option;

      if (translateType(option) === 'dropdown') {
        var field = this.dropdownInputFor(option);
        if (field) {
          var match = String(field.value || '').match(/[\[(]\s*\+?\s*£?\s*([\d,]+(?:\.\d+)?)\s*[\])]/);
          if (match) return parseFloat(match[1].replace(/,/g, ''));
        }
        return null;
      }

      // The ticked input for this very value, not merely the group's first
      // tick, since an option allowing several picks has more than one.
      var checked = this.checkedInputsFor(option).filter(function (input) {
        return inputLabel(input) === found.value.label;
      })[0];
      if (checked) {
        var priceEl = this.priceElementNear(checked);
        if (priceEl) {
          var money = String(priceEl.textContent || '').match(/([\d,]+(?:\.\d+)?)/);
          if (money) return parseFloat(money[1].replace(/,/g, '')) * this.quantityNear(checked);
        }
      }
      return null;
    },

    /**
     * Climbs from a checked input up through its own ancestors, remembering
     * the closest price tag found along the way, and stops as soon as it
     * reaches a level that also covers a sibling value (recognised by that
     * level suddenly containing more than one radio/checkbox) — the same
     * climb the floating Add to Cart button's own script already relies on,
     * since buttons and swatches keep a value's price at different
     * distances from its input depending on the option's own layout.
     */
    priceElementNear: function (checked) {
      var node = checked.parentElement;
      var found = null;
      var depth = 0;
      while (node && depth < 10) {
        if (node.querySelectorAll('input[type="radio"], input[type="checkbox"]').length > 1) break;
        var priceEl = node.querySelector('.tpo_additional-price');
        if (priceEl) found = priceEl;
        node = node.parentElement;
        depth++;
      }
      return found;
    },

    /**
     * Required options Easify is showing that have nothing chosen yet.
     * Used for build progress and for the check before add to cart.
     */
    getOutstanding: function () {
      var self = this;
      var chosenKeys = {};
      this.getSelection().forEach(function (item) { chosenKeys[item.optionKey] = true; });
      return this.discoverOptions().filter(function (option) {
        return option.required && self.isVisible(option) && !chosenKeys[option.key];
      });
    },

    /** Build progress as chosen / total of the required options on screen now. */
    getProgress: function () {
      var self = this;
      var required = this.discoverOptions().filter(function (option) {
        return option.required && self.isVisible(option);
      });
      var outstanding = this.getOutstanding().length;
      return {
        total: required.length,
        complete: required.length - outstanding,
        outstanding: outstanding
      };
    },

    /* ---- pricing --------------------------------------------------
     * The floating bar is Wattec's price display and is deliberately the
     * number the customer sees, so the builder reads that number rather than
     * publishing a competing one. The breakdown below is only for showing
     * which component contributed what — the total always comes from the bar.
     * -------------------------------------------------------------- */

    /** The price currently on display, exactly as the customer sees it. */
    getDisplayedPrice: function () {
      var element = document.querySelector('#wattec-bar .wb-price');
      return element ? element.textContent.trim() : null;
    },

    /** The displayed price as a number, for reconciling against the breakdown. */
    getDisplayedAmount: function () {
      var text = this.getDisplayedPrice();
      if (!text) return null;
      var digits = text.replace(/[^0-9.]/g, '');
      return digits ? parseFloat(digits) : null;
    },

    /**
     * Which component contributed what. The total is the price already on the
     * page — this only explains it, and never overrides it.
     *
     * Hidden lines are listed separately: an option Easify has hidden can
     * still hold a selection from earlier, and it is still part of the price.
     */
    getPriceBreakdown: function () {
      var base = toNumber(config().basePrice) / 100;
      var selection = this.getSelection();
      var lines = selection.filter(function (item) { return item.visible; });
      var hidden = selection.filter(function (item) { return !item.visible && item.price; });

      function sum(items) {
        return items.reduce(function (total, item) { return total + item.price; }, 0);
      }

      var addons = sum(lines);
      var hiddenAddons = sum(hidden);
      var displayed = this.getDisplayedAmount();
      // Easify leaves hidden choices out of the cart, so they are listed for
      // information only and never added to what the build costs.
      var accounted = base + addons;

      return {
        base: base,
        lines: lines,
        hidden: hidden,
        addons: addons,
        hiddenAddons: hiddenAddons,
        accounted: accounted,
        total: displayed === null ? accounted : displayed,
        displayed: this.getDisplayedPrice(),
        displayedAmount: displayed,
        reconciles: displayed === null ? null : Math.abs(displayed - accounted) < 0.01,
        unaccounted: displayed === null ? 0 : displayed - accounted
      };
    },

    /* ---- changing the build --------------------------------------- */

    /**
     * Choose a value by driving the control Easify rendered, so Easify runs
     * its own change handling: price, conditional logic and cart data all
     * update as though the customer clicked it themselves.
     */
    setSelection: function (valueId) {
      var options = this.discoverOptions();
      for (var i = 0; i < options.length; i++) {
        var option = options[i];
        var value = option.values.filter(function (v) { return v.id === valueId; })[0];
        if (!value) continue;

        var el = this.elementFor(option);
        if (!el) return false;

        if (translateType(option) === 'dropdown') {
          return this.chooseDropdownValue(el, value.label);
        }

        var inputs = el.querySelectorAll('input[type="radio"], input[type="checkbox"]');
        for (var k = 0; k < inputs.length; k++) {
          if (inputLabel(inputs[k]) !== value.label || inputs[k].disabled) continue;
          var label = inputs[k].closest('label') || (inputs[k].id
            ? el.querySelector('label[for="' + inputs[k].id + '"]') : null);
          press(label || inputs[k]);
          return true;
        }
        return false;
      }
      return false;
    },

    /**
     * Sets how many of an already-chosen value the customer wants, through
     * the quantity box Easify drew for it, and tells Easify the same way a
     * customer typing into the box does. Used when a saved build is loaded.
     */
    setQuantity: function (valueId, quantity) {
      var found = this.findValue(valueId);
      if (!found) return false;
      var checked = this.checkedInputsFor(found.option).filter(function (input) {
        return inputLabel(input) === found.value.label;
      })[0];
      var box = checked ? this.quantityBoxNear(checked) : null;
      if (!box) return false;
      var wanted = String(Math.max(1, parseInt(quantity, 10) || 1));
      if (box.value === wanted) return true;
      box.value = wanted;
      box.dispatchEvent(new Event('input', { bubbles: true }));
      box.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    },

    /**
     * Opens a "dropdown" group's own picker and presses the card for the
     * component wanted. Easify pre-renders every card in the list and only
     * toggles the wrapper's hidden state on open, so the card is already in
     * the DOM to find — nothing here waits on a re-render.
     *
     * A card's text is the component's name followed by its spec line and
     * price, so it is matched on its opening words rather than in full.
     */
    chooseDropdownValue: function (groupEl, label) {
      var field = groupEl.querySelector('input.tpo_option-dropdown, input[type="text"]');
      if (field) press(field);

      var wanted = flatten(label).toLowerCase();
      var cards = groupEl.querySelectorAll(
        '.tpo_option-type-selection, li, [role="option"]'
      );
      for (var i = 0; i < cards.length; i++) {
        if (cards[i].classList && cards[i].classList.contains('tpo_option-type-selection--empty')) continue;
        var text = flatten(cards[i].textContent).toLowerCase();
        if (!text) continue;
        if (text === wanted || text.indexOf(wanted) === 0 || flatten(parsePickedLabel(text)).toLowerCase() === wanted) {
          press(cards[i]);
          return true;
        }
      }

      // Nothing matched: leave the picker as it was found rather than open.
      if (field) press(field);
      return false;
    },

    /* ---- opening a group's own picker ------------------------------
     * Easify shows and hides a dropdown group's list itself, marking a
     * closed one with the plain "hidden" attribute and closing it as soon
     * as a component is chosen. These two only ever read that state or
     * press the field the customer would press themselves, so Easify stays
     * in charge of the list: nothing here forces it open or shut.
     * -------------------------------------------------------------- */

    /** Is this group's list of components currently on screen? */
    isPickerOpen: function (option) {
      var el = this.elementFor(option);
      var wrapper = el && el.querySelector('.tpo_option-type-dropdown-wrapper');
      return !!(wrapper && !wrapper.hasAttribute('hidden'));
    },

    /** Opens a closed list. Returns false for a group that has no list of
     *  its own (the button groups) or one that is already open. */
    openPicker: function (option) {
      var el = this.elementFor(option);
      if (!el) return false;
      var wrapper = el.querySelector('.tpo_option-type-dropdown-wrapper');
      if (!wrapper || !wrapper.hasAttribute('hidden')) return false;
      var field = el.querySelector('input.tpo_option-dropdown');
      if (!field) return false;
      press(field);
      return true;
    },

    /* ---- validation and cart -------------------------------------- */

    /** Is the build complete enough to add to the cart? */
    validate: function () {
      var outstanding = this.getOutstanding();
      return {
        valid: outstanding.length === 0,
        missing: outstanding.map(function (option) {
          return { label: option.label, section: option.sectionTitle };
        })
      };
    },

    /**
     * Hand over to the existing add to cart. Easify attaches its own
     * configuration to the real form, and the floating Add to Cart button's
     * own investigation found that only a genuine, native click on that
     * form's real submit control reliably carries Easify's add-on pricing
     * through — so, as before, the only safe way to add the build is to
     * press the button that is already there.
     */
    submit: function () {
      var check = this.validate();
      if (!check.valid) return check;
      var button = document.querySelector(
        '[name="add"], .product-form__submit, form[action*="/cart/add"] button[type="submit"]'
      );
      // Tested on this page: a script-driven press of this button leaves the
      // cart empty, while the customer's own press adds the build with its
      // full Easify pricing. So this only brings the button to them.
      if (button && !button.disabled) button.scrollIntoView({ block: 'center', behavior: 'smooth' });
      return check;
    },

    /* ---- staying in step with Easify -------------------------------
     * A MutationObserver on Easify's own container catches every native
     * input change and re-render, and a light poll on top catches anything
     * that touches the page without mutating that subtree (matching the
     * belt-and-braces approach already proven in the floating Add to Cart
     * button's own price tracking) — there is no single confirmed
     * "selection changed" event from Easify to rely on instead.
     * -------------------------------------------------------------- */

    observeChanges: function (callback, debounceMs) {
      var wait = typeof debounceMs === 'number' ? debounceMs : 60;
      var timer = null;
      var root = container();

      function fire(reason) {
        clearTimeout(timer);
        timer = setTimeout(function () { callback(reason); }, wait);
      }

      function onInput() { fire({ type: 'input' }); }

      if (root) root.addEventListener('change', onInput, true);

      var observer = null;
      if (root && window.MutationObserver) {
        observer = new MutationObserver(function () { fire({ type: 'render' }); });
        observer.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['style', 'class', 'disabled', 'value'] });
      }

      var priceObserver = null;
      var priceNode = document.querySelector('#wattec-bar .wb-price');
      if (priceNode && window.MutationObserver) {
        priceObserver = new MutationObserver(function () { fire({ type: 'price' }); });
        priceObserver.observe(priceNode, { childList: true, characterData: true, subtree: true });
      }

      // Belt-and-braces: a plain poll catches a selection made through an
      // interaction the observer above does not happen to see.
      var poll = setInterval(function () { fire({ type: 'poll' }); }, 700);

      return function unsubscribe() {
        clearTimeout(timer);
        clearInterval(poll);
        if (root) root.removeEventListener('change', onInput, true);
        if (observer) observer.disconnect();
        if (priceObserver) priceObserver.disconnect();
      };
    }
  };

  window.WattecPCBuilder = window.WattecPCBuilder || {};
  window.WattecPCBuilder.aris = Easify;
  window.WattecPCBuilder.sections = SECTIONS;
  window.WattecPCBuilder.version = VERSION;

  /* ==================================================================
   * Adapter check panel
   *
   * A small on-page diagnostic, useful for confirming the adapter above is
   * actually finding and reading Easify's controls correctly. It only ever
   * appears when ?builder-check=1 is on the URL, and needed no changes for
   * this migration — it was already written against the same adapter
   * interface as everything else.
   * ================================================================== */

  if (window.location.search.indexOf('builder-check=1') === -1) return;

  function money(amount) {
    var currency = config().currency || 'GBP';
    try {
      return amount.toLocaleString('en-GB', { style: 'currency', currency: currency });
    } catch (error) {
      return '£' + amount.toFixed(2);
    }
  }

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  function buildPanel() {
    var panel = el('div', 'wpb-check');
    panel.innerHTML = [
      '<div class="wpb-check__bar">',
      '  <span class="wpb-check__dot"></span>',
      '  <strong>Builder adapter check</strong>',
      '  <span class="wpb-check__ver">v' + VERSION + '</span>',
      '  <button type="button" class="wpb-check__min" aria-label="Minimise">–</button>',
      '</div>',
      '<div class="wpb-check__body">',
      '  <div class="wpb-check__grid" data-role="stats"></div>',
      '  <div class="wpb-check__price" data-role="price"></div>',
      '  <div class="wpb-check__list" data-role="selection"></div>',
      '  <div class="wpb-check__log" data-role="log"></div>',
      '  <button type="button" class="wpb-check__test" data-role="test">Run selection test</button>',
      '</div>'
    ].join('');
    document.body.appendChild(panel);

    panel.querySelector('.wpb-check__min').addEventListener('click', function () {
      panel.classList.toggle('is-min');
    });

    return panel;
  }

  function renderStats(target, adapter) {
    var options = adapter.discoverOptions();
    var visible = options.filter(function (option) { return adapter.isVisible(option); });
    var progress = adapter.getProgress();
    var values = options.reduce(function (sum, option) { return sum + option.values.length; }, 0);

    var stats = [
      ['Options found', options.length],
      ['Showing now', visible.length],
      ['Values', values],
      ['Required done', progress.complete + ' / ' + progress.total]
    ];

    target.innerHTML = '';
    stats.forEach(function (pair) {
      var cell = el('div', 'wpb-check__stat');
      cell.appendChild(el('span', 'wpb-check__stat-k', pair[0]));
      cell.appendChild(el('span', 'wpb-check__stat-v', String(pair[1])));
      target.appendChild(cell);
    });
  }

  function renderPrice(target, adapter) {
    var breakdown = adapter.getPriceBreakdown();
    target.innerHTML = '';

    var rows = [
      ['Total on the page', breakdown.displayed || 'not found'],
      ['Base product', money(breakdown.base)],
      ['Components showing', money(breakdown.addons)]
    ];

    if (breakdown.hiddenAddons) {
      rows.push(['Components hidden but still priced', money(breakdown.hiddenAddons)]);
    }

    rows.forEach(function (pair) {
      var row = el('div', 'wpb-check__row');
      row.appendChild(el('span', null, pair[0]));
      row.appendChild(el('b', null, String(pair[1])));
      target.appendChild(row);
    });

    var verdict;
    if (breakdown.reconciles === null) {
      verdict = el('div', 'wpb-check__verdict is-warn', 'No price found on the page to read.');
    } else if (breakdown.reconciles) {
      verdict = el('div', 'wpb-check__verdict is-ok',
        'The breakdown adds up to the price on the page exactly.');
    } else {
      verdict = el('div', 'wpb-check__verdict is-warn',
        'The page shows ' + money(Math.abs(breakdown.unaccounted)) +
        (breakdown.unaccounted > 0 ? ' more' : ' less') +
        ' than the components listed below account for.');
    }
    target.appendChild(verdict);
  }

  function renderSelection(target, adapter) {
    var all = adapter.getSelection();
    var selection = all.filter(function (item) { return item.visible; });
    var hidden = all.filter(function (item) { return !item.visible && item.price; });
    target.innerHTML = '';

    if (!selection.length) {
      target.appendChild(el('div', 'wpb-check__empty', 'Nothing chosen yet.'));
      return;
    }

    function addRow(item, isHidden) {
      var row = el('div', 'wpb-check__pick' + (isHidden ? ' is-hidden' : ''));
      row.appendChild(el('span', 'wpb-check__pick-s', item.sectionTitle));
      row.appendChild(el('span', 'wpb-check__pick-v', item.label));
      row.appendChild(el('span', 'wpb-check__pick-p', item.price ? '+' + money(item.price) : 'included'));
      target.appendChild(row);
    }

    selection.forEach(function (item) { addRow(item, false); });

    if (hidden.length) {
      target.appendChild(el('div', 'wpb-check__todo',
        'Chosen earlier, no longer on screen, still charged:'));
      hidden.forEach(function (item) { addRow(item, true); });
    }

    var outstanding = adapter.getOutstanding();
    if (outstanding.length) {
      var note = el('div', 'wpb-check__todo', 'Still needed: ' + outstanding.map(function (option) {
        return option.label;
      }).join(', '));
      target.appendChild(note);
    }
  }

  function log(target, message) {
    var line = el('div', 'wpb-check__log-line', new Date().toLocaleTimeString('en-GB') + '  ' + message);
    target.insertBefore(line, target.firstChild);
    while (target.children.length > 6) target.removeChild(target.lastChild);
  }

  function boot() {
    var adapter = window.WattecPCBuilder.aris;
    adapter.ready().then(function () {
      var panel = buildPanel();
      var stats = panel.querySelector('[data-role="stats"]');
      var price = panel.querySelector('[data-role="price"]');
      var selection = panel.querySelector('[data-role="selection"]');
      var logTarget = panel.querySelector('[data-role="log"]');

      function render() {
        renderStats(stats, adapter);
        renderPrice(price, adapter);
        renderSelection(selection, adapter);
      }

      render();
      log(logTarget, 'Adapter ready.');
      adapter.observeChanges(function (reason) {
        render();
        log(logTarget, 'Update (' + reason.type + ')');
      });

      panel.querySelector('[data-role="test"]').addEventListener('click', function () {
        var options = adapter.discoverOptions().filter(function (o) { return adapter.isVisible(o) && o.values.length; });
        if (!options.length) { log(logTarget, 'Nothing visible to test.'); return; }
        var option = options[Math.floor(Math.random() * options.length)];
        var value = option.values[Math.floor(Math.random() * option.values.length)];
        var ok = adapter.setSelection(value.id);
        log(logTarget, (ok ? 'Selected ' : 'Failed to select ') + option.label + ' → ' + value.label);
      });
    }).catch(function (error) {
      if (window.console) console.warn('[Wattec builder check]', error.message);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
